import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Building2, Briefcase, Package, Loader2, Search, Download, Check, Plus, X, Edit3, Trash2, DollarSign, Users, Hash, Eye, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, RefreshCw, FileSpreadsheet, BarChart3, FileText, CheckSquare, Square, Languages, FileCheck, Globe, User, CreditCard, ToggleLeft, ToggleRight, Shield, Save, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { DirhamPrice } from '../components/DirhamPrice';
import { printRakezActivity } from '../utils/printRakezActivity';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Document Toggle Row Component
const DocumentToggleRow = ({ icon, label, isActive, onToggle }) => (
  <div className="flex items-center justify-between p-3 bg-[#0a0a0a] rounded-xl">
    <div className="flex items-center gap-3">
      {icon}
      <span className="text-white">{label}</span>
    </div>
    <button
      onClick={onToggle}
      className={`relative w-14 h-7 rounded-full transition-all duration-300 ${
        isActive ? 'bg-emerald-500' : 'bg-red-500/50'
      }`}
    >
      <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all duration-300 shadow-md ${
        isActive ? 'right-1' : 'left-1'
      }`} />
    </button>
  </div>
);

// دوال التحقق من اللغة
const isArabicText = (text) => {
  if (!text) return true;
  const arabicRegex = /^[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF\s\d\-_.,:;!?()]+$/;
  return arabicRegex.test(text);
};

const isEnglishText = (text) => {
  if (!text) return true;
  const englishRegex = /^[a-zA-Z\s\d\-_.,:;!?()]+$/;
  return englishRegex.test(text);
};

export default function RAKEZSetup() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { language } = useLanguage();
  
  const [activeView, setActiveView] = useState('main');
  const [activities, setActivities] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [importingExcel, setImportingExcel] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalActivities, setTotalActivities] = useState(0);
  const [activitiesPerPage] = useState(50);
  const [goToPageInput, setGoToPageInput] = useState('');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [licenseTypeFilter, setLicenseTypeFilter] = useState('all');
  const [availableLicenseTypes, setAvailableLicenseTypes] = useState([]);
  
  // Selection
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActivating, setBulkActivating] = useState(false);
  const [bulkDeactivating, setBulkDeactivating] = useState(false);
  
  // Details Modal
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  
  // Print Language Chooser
  const [printTarget, setPrintTarget] = useState(null);
  
  // Activity Modal State
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [savingActivity, setSavingActivity] = useState(false);
  const [newActivity, setNewActivity] = useState({
    name_ar: '',
    name_en: '',
    code: '',
    category: 'عام',
    is_active: true,
    license_type: '',
    license_type_ar: '',
    zone: '',
    activity_group: '',
    activity_group_ar: '',
    gics_business_sector: '',
    gics_subsector: '',
    description_en: '',
    description_ar: '',
    minimum_share_capital: '',
    activity_price: '',
    qualification_requirement: '',
    documents_required: '',
    rakez_hse_risk: '',
    compliance_risk_rating: '',
    is_special: '',
    is_not_allowed_coworking: '',
  });
  
  // Package Modal State
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [savingPackage, setSavingPackage] = useState(false);
  const [translatingField, setTranslatingField] = useState(null);
  const [newPackage, setNewPackage] = useState({
    name_ar: '',
    name_en: '',
    notes_ar: '',
    notes_en: '',
    activities_count: 1,
    visas_count: 0,
    price_type: 'fixed',
    price_fixed: '',
    price_min: '',
    price_max: '',
    is_active: true
  });
  
  // Validation errors
  const [validationErrors, setValidationErrors] = useState({});
  
  // Required Documents State
  const [documentsConfig, setDocumentsConfig] = useState({
    inside_uae: null,
    outside_uae: null
  });
  const [countriesList, setCountriesList] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [savingDocuments, setSavingDocuments] = useState(false);
  const [selectedCountriesInside, setSelectedCountriesInside] = useState([]);
  const [selectedCountriesOutside, setSelectedCountriesOutside] = useState([]);
  const [documentsInvestorType, setDocumentsInvestorType] = useState(null); // 'inside_uae' or 'outside_uae'
  
  // Master Documents from Central List
  const [masterDocuments, setMasterDocuments] = useState([]);
  const [enabledDocuments, setEnabledDocuments] = useState([]); // IDs of enabled documents for current investor type
  
  // Resident ID Option (الهوية الصادرة من دولة المقيم)
  const [residentIdOption, setResidentIdOption] = useState({
    enabled_globally: false,
    enabled_for_service: false,
    nationalities: []
  });
  const [allNationalities, setAllNationalities] = useState([]);
  const [nationalitySearch, setNationalitySearch] = useState('');
  
  const headers = { Authorization: `Bearer ${token}` };

  // دالة الترجمة التلقائية
  const translateText = useCallback(async (text, sourceLang, targetLang, targetField) => {
    if (!text || !text.trim()) return;
    
    setTranslatingField(targetField);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/translate`, {
        text: text,
        source_lang: sourceLang,
        target_lang: targetLang
      }, { headers });
      
      if (res.data.success && res.data.translated_text) {
        setNewPackage(prev => ({
          ...prev,
          [targetField]: res.data.translated_text
        }));
      }
    } catch (err) {
      console.error('Translation error:', err);
      toast.error(language === 'ar' ? 'فشل في الترجمة التلقائية' : 'Auto-translation failed');
    } finally {
      setTranslatingField(null);
    }
  }, [headers, language]);

  // التحقق من صحة الحقول
  const validateField = (fieldName, value) => {
    const errors = { ...validationErrors };
    
    if (fieldName === 'name_ar' || fieldName === 'notes_ar') {
      if (value && !isArabicText(value)) {
        errors[fieldName] = language === 'ar' ? 'يجب إدخال نص عربي فقط' : 'Arabic text only';
      } else {
        delete errors[fieldName];
      }
    }
    
    if (fieldName === 'name_en' || fieldName === 'notes_en') {
      if (value && !isEnglishText(value)) {
        errors[fieldName] = language === 'ar' ? 'يجب إدخال نص إنجليزي فقط' : 'English text only';
      } else {
        delete errors[fieldName];
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // تحديث حقل البكج مع التحقق
  const updatePackageField = (fieldName, value) => {
    setNewPackage(prev => ({ ...prev, [fieldName]: value }));
    validateField(fieldName, value);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (activeView === 'activities') {
      fetchActivities();
    } else if (activeView === 'packages') {
      fetchPackages();
    } else if (activeView === 'documents') {
      fetchDocumentsConfig();
      fetchCountriesList();
      fetchNationalities();
    }
  }, [activeView, currentPage, statusFilter, licenseTypeFilter]);

  // Reset selection when page changes
  useEffect(() => {
    setSelectedActivities([]);
    setSelectAll(false);
  }, [currentPage]);

  // Instant search — debounce searchQuery changes to trigger fetch after
  // the user pauses typing. Only active on the activities view.
  useEffect(() => {
    if (activeView !== 'activities') return undefined;
    const handle = setTimeout(() => {
      // Reset to first page whenever the query changes so the user sees
      // matches from the top.
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchActivities();
      }
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const fetchActivities = async () => {
    setLoadingActivities(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: activitiesPerPage,
        search: searchQuery,
        status: statusFilter,
        license_type: licenseTypeFilter
      });
      
      const res = await axios.get(`${BACKEND_URL}/api/admin/rakez-activities?${params}`, { headers });
      setActivities(res.data.data || []);
      setTotalPages(res.data.total_pages || 1);
      setTotalActivities(res.data.total || 0);
      setAvailableLicenseTypes(res.data.filters?.license_types || []);
    } catch (err) {
      console.error('Error fetching activities:', err);
      toast.error(language === 'ar' ? 'فشل في جلب الأنشطة' : 'Failed to fetch activities');
    } finally {
      setLoadingActivities(false);
    }
  };

  const fetchPackages = async () => {
    setLoadingPackages(true);
    try {
      // iter-249 — Merge legacy rakez_packages + new formation_packages
      // (authority=rakez). New packages are the canonical source going
      // forward; legacy ones remain read-only for reference/migration.
      const [legacyRes, newRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/rakez-packages`, { headers }).catch(() => ({ data: { data: [] } })),
        axios.get(`${BACKEND_URL}/api/admin/formation-packages?authority=rakez&limit=200`, { headers }).catch(() => ({ data: { data: [] } })),
      ]);
      const legacy = (legacyRes.data.data || []).map((p) => ({ ...p, _source: 'legacy' }));
      const newer  = (newRes.data.data   || []).map((p) => ({ ...p, _source: 'new', authority: p.authority || 'rakez' }));
      setPackages([...newer, ...legacy]);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setPackages([]);
    } finally {
      setLoadingPackages(false);
    }
  };

  // =============== Required Documents Functions ===============
  
  // جلب الأوراق المطلوبة من القائمة المركزية
  const fetchMasterDocuments = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/master-documents`, { headers });
      if (res.data.success) {
        setMasterDocuments(res.data.data.filter(d => d.is_active));
      }
    } catch (err) {
      console.error('Error fetching master documents:', err);
    }
  };

  // جلب قائمة الجنسيات
  const fetchNationalities = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/master-documents/nationalities`, { headers });
      if (res.data.success) {
        setAllNationalities(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching nationalities:', err);
    }
  };

  // جلب الأوراق المفعّلة لنوع المستثمر
  const fetchEnabledDocumentsForInvestor = async (investorType) => {
    try {
      const serviceId = `rakez_${investorType}`;
      const res = await axios.get(`${BACKEND_URL}/api/master-documents/service/rakez/${serviceId}`, { headers });
      if (res.data.success) {
        const enabledIds = res.data.data.filter(d => d.is_enabled).map(d => d.id);
        setEnabledDocuments(enabledIds);
        
        // تحديث خيار الهوية من دولة المقيم
        if (res.data.resident_id_option) {
          setResidentIdOption(res.data.resident_id_option);
        }
      }
    } catch (err) {
      console.error('Error fetching enabled documents:', err);
      setEnabledDocuments([]);
      setResidentIdOption({ enabled_globally: false, enabled_for_service: false, nationalities: [] });
    }
  };

  // تحديث الأوراق المفعّلة لنوع المستثمر
  const saveEnabledDocuments = async (investorType) => {
    setSavingDocuments(true);
    try {
      const serviceId = `rakez_${investorType}`;
      await axios.post(`${BACKEND_URL}/api/master-documents/service/rakez/${serviceId}`, {
        enabled_documents: enabledDocuments,
        resident_id_enabled: residentIdOption.enabled_for_service,
        resident_id_nationalities: residentIdOption.nationalities
      }, { headers });
      toast.success(language === 'ar' ? 'تم حفظ الأوراق المطلوبة بنجاح' : 'Required documents saved successfully');
    } catch (err) {
      console.error('Error saving enabled documents:', err);
      toast.error(language === 'ar' ? 'فشل في حفظ الأوراق' : 'Failed to save documents');
    } finally {
      setSavingDocuments(false);
    }
  };

  // Toggle document enabled state
  const toggleDocumentEnabled = (docId) => {
    setEnabledDocuments(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };
  
  const fetchDocumentsConfig = async () => {
    setLoadingDocuments(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/rakez-documents`, { headers });
      if (res.data.success) {
        setDocumentsConfig(res.data.data);
        setSelectedCountriesInside(res.data.data.inside_uae?.national_id_countries || []);
        setSelectedCountriesOutside(res.data.data.outside_uae?.national_id_countries || []);
      }
    } catch (err) {
      console.error('Error fetching documents config:', err);
      toast.error(language === 'ar' ? 'فشل في جلب إعدادات الأوراق' : 'Failed to fetch documents config');
    } finally {
      setLoadingDocuments(false);
    }
  };

  const fetchCountriesList = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/countries`, { headers });
      if (res.data.success) {
        setCountriesList(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching countries:', err);
    }
  };

  const toggleDocumentsStatus = async (investorType) => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/rakez-documents/${investorType}/toggle`, {}, { headers });
      if (res.data.success) {
        setDocumentsConfig(prev => ({
          ...prev,
          [investorType === 'inside_uae' ? 'inside_uae' : 'outside_uae']: {
            ...prev[investorType === 'inside_uae' ? 'inside_uae' : 'outside_uae'],
            is_active: res.data.is_active
          }
        }));
        toast.success(language === 'ar' ? 'تم تحديث الحالة بنجاح' : 'Status updated successfully');
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      toast.error(language === 'ar' ? 'فشل في تحديث الحالة' : 'Failed to update status');
    }
  };

  const saveDocumentsConfig = async (investorType) => {
    setSavingDocuments(true);
    try {
      const config = documentsConfig[investorType];
      const selectedCountries = investorType === 'inside_uae' ? selectedCountriesInside : selectedCountriesOutside;
      
      const payload = {
        investor_type: investorType,
        personal_photo: config.personal_photo,
        passport_copy: config.passport_copy,
        uae_id_copy: investorType === 'inside_uae' ? config.uae_id_copy : null,
        national_id_required: selectedCountries.length > 0,
        national_id_countries: selectedCountries,
        bank_statement_6_months: config.bank_statement_6_months || false,
        bank_statement_7_months: config.bank_statement_7_months || false,
        criminal_record_check: config.criminal_record_check || false,
        parent_company_license: config.parent_company_license || false,
        is_active: config.is_active
      };
      
      const res = await axios.put(`${BACKEND_URL}/api/admin/rakez-documents/${investorType}`, payload, { headers });
      if (res.data.success) {
        toast.success(language === 'ar' ? 'تم حفظ الإعدادات بنجاح' : 'Settings saved successfully');
        fetchDocumentsConfig();
      }
    } catch (err) {
      console.error('Error saving documents config:', err);
      toast.error(language === 'ar' ? 'فشل في حفظ الإعدادات' : 'Failed to save settings');
    } finally {
      setSavingDocuments(false);
    }
  };

  const updateDocumentField = (investorType, field, value) => {
    setDocumentsConfig(prev => ({
      ...prev,
      [investorType]: {
        ...prev[investorType],
        [field]: value
      }
    }));
  };

  const importFromExcel = async () => {
    setImportingExcel(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/rakez-activities/import-excel`, {}, { headers });
      toast.success(language === 'ar' 
        ? `تم استيراد ${res.data.count} نشاط بنجاح` 
        : `Successfully imported ${res.data.count} activities`
      );
      setCurrentPage(1);
      fetchActivities();
    } catch (err) {
      console.error('Error importing:', err);
      toast.error(language === 'ar' ? 'فشل في استيراد الأنشطة' : 'Failed to import activities');
    } finally {
      setImportingExcel(false);
    }
  };

  const exportToExcel = async () => {
    setExportingExcel(true);
    try {
      const params = new URLSearchParams({
        status: statusFilter,
        license_type: licenseTypeFilter
      });
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/rakez-activities/export/excel?${params}`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `RAKEZ_Activities_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'ar' ? 'تم تحميل ملف Excel بنجاح' : 'Excel file downloaded successfully');
    } catch (err) {
      console.error('Error exporting Excel:', err);
      toast.error(language === 'ar' ? 'فشل في تحميل ملف Excel' : 'Failed to download Excel file');
    } finally {
      setExportingExcel(false);
    }
  };

  const exportToPDF = async () => {
    setExportingPDF(true);
    try {
      const params = new URLSearchParams({
        status: statusFilter,
        license_type: licenseTypeFilter
      });
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/rakez-activities/export/pdf?${params}`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `RAKEZ_Activities_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'ar' ? 'تم تحميل ملف PDF بنجاح' : 'PDF file downloaded successfully');
    } catch (err) {
      console.error('Error exporting PDF:', err);
      toast.error(language === 'ar' ? 'فشل في تحميل ملف PDF' : 'Failed to download PDF file');
    } finally {
      setExportingPDF(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchActivities();
  };

  const goToPage = (page) => {
    const pageNum = parseInt(page);
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      setGoToPageInput('');
    }
  };

  const handleGoToPageSubmit = (e) => {
    e.preventDefault();
    goToPage(goToPageInput);
  };

  // Selection Functions
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedActivities([]);
      setSelectAll(false);
    } else {
      setSelectedActivities(activities.map(a => a.id));
      setSelectAll(true);
    }
  };

  const toggleSelectActivity = (activityId) => {
    if (selectedActivities.includes(activityId)) {
      setSelectedActivities(selectedActivities.filter(id => id !== activityId));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedActivities, activityId];
      setSelectedActivities(newSelected);
      if (newSelected.length === activities.length) {
        setSelectAll(true);
      }
    }
  };

  const bulkActivate = async () => {
    if (selectedActivities.length === 0) {
      toast.error(language === 'ar' ? 'يرجى تحديد الأنشطة أولاً' : 'Please select activities first');
      return;
    }

    setBulkActivating(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/rakez-activities/bulk/activate`, selectedActivities, { headers });
      toast.success(language === 'ar' 
        ? `تم تفعيل ${res.data.modified_count} نشاط بنجاح` 
        : `Successfully activated ${res.data.modified_count} activities`
      );
      setSelectedActivities([]);
      setSelectAll(false);
      fetchActivities();
    } catch (err) {
      console.error('Error bulk activating:', err);
      toast.error(language === 'ar' ? 'فشل في تفعيل الأنشطة' : 'Failed to activate activities');
    } finally {
      setBulkActivating(false);
    }
  };

  const bulkDeactivate = async () => {
    if (selectedActivities.length === 0) {
      toast.error(language === 'ar' ? 'يرجى تحديد الأنشطة أولاً' : 'Please select activities first');
      return;
    }

    setBulkDeactivating(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/rakez-activities/bulk/deactivate`, selectedActivities, { headers });
      toast.success(language === 'ar' 
        ? `تم إلغاء تفعيل ${res.data.modified_count} نشاط بنجاح` 
        : `Successfully deactivated ${res.data.modified_count} activities`
      );
      setSelectedActivities([]);
      setSelectAll(false);
      fetchActivities();
    } catch (err) {
      console.error('Error bulk deactivating:', err);
      toast.error(language === 'ar' ? 'فشل في إلغاء تفعيل الأنشطة' : 'Failed to deactivate activities');
    } finally {
      setBulkDeactivating(false);
    }
  };

  const openDetailsModal = (activity) => {
    setSelectedActivity(activity);
    setShowDetailsModal(true);
  };

  // Activity Functions
  const openAddActivityModal = () => {
    setEditingActivity(null);
    setNewActivity({
      name_ar: '',
      name_en: '',
      code: '',
      category: 'عام',
      is_active: true,
      license_type: '',
      license_type_ar: '',
      zone: '',
      activity_group: '',
      activity_group_ar: '',
      gics_business_sector: '',
      gics_subsector: '',
      description_en: '',
      description_ar: '',
      minimum_share_capital: '',
      activity_price: '',
      qualification_requirement: '',
      documents_required: '',
      rakez_hse_risk: '',
      compliance_risk_rating: '',
      is_special: '',
      is_not_allowed_coworking: '',
    });
    setShowActivityModal(true);
  };

  const openEditActivityModal = (activity) => {
    setEditingActivity(activity);
    setNewActivity({
      name_ar: activity.activity_name_ar || '',
      name_en: activity.activity_name || '',
      code: activity.activity_code || '',
      category: activity.activity_group || 'عام',
      is_active: activity.status === 'Active',
      license_type: activity.license_type || '',
      license_type_ar: activity.license_type_ar || '',
      zone: activity.zone || '',
      activity_group: activity.activity_group || '',
      activity_group_ar: activity.activity_group_ar || '',
      gics_business_sector: activity.gics_business_sector || '',
      gics_subsector: activity.gics_subsector || '',
      description_en: activity.description_en || '',
      description_ar: activity.description_ar || '',
      minimum_share_capital: activity.minimum_share_capital || '',
      activity_price: activity.activity_price || '',
      qualification_requirement: activity.qualification_requirement || '',
      documents_required: activity.documents_required || '',
      rakez_hse_risk: activity.rakez_hse_risk || '',
      compliance_risk_rating: activity.compliance_risk_rating || '',
      is_special: activity.is_special || '',
      is_not_allowed_coworking: activity.is_not_allowed_coworking || '',
    });
    setShowActivityModal(true);
  };

  const saveActivity = async () => {
    if (!newActivity.name_ar || !newActivity.name_en) {
      toast.error(language === 'ar' ? 'يرجى إدخال اسم النشاط' : 'Please enter activity name');
      return;
    }

    setSavingActivity(true);
    try {
      if (editingActivity) {
        await axios.put(`${BACKEND_URL}/api/admin/rakez-activities/${editingActivity.id}`, newActivity, { headers });
        toast.success(language === 'ar' ? 'تم تحديث النشاط بنجاح' : 'Activity updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/rakez-activities`, newActivity, { headers });
        toast.success(language === 'ar' ? 'تم إضافة النشاط بنجاح' : 'Activity added successfully');
      }

      setShowActivityModal(false);
      fetchActivities();
    } catch (err) {
      const detail = err.response?.data?.detail;
      let errorMessage = language === 'ar' ? 'فشل في حفظ النشاط' : 'Failed to save activity';
      if (typeof detail === 'string') errorMessage = detail;
      else if (Array.isArray(detail)) errorMessage = detail.map(d => d.msg || d).join(', ');
      toast.error(errorMessage);
    } finally {
      setSavingActivity(false);
    }
  };

  const deleteActivity = async (activityId) => {
    if (!confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا النشاط؟' : 'Are you sure you want to delete this activity?')) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/rakez-activities/${activityId}`, { headers });
      toast.success(language === 'ar' ? 'تم حذف النشاط بنجاح' : 'Activity deleted successfully');
      fetchActivities();
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في حذف النشاط' : 'Failed to delete activity');
    }
  };

  const toggleActivityStatus = async (activity) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/rakez-activities/${activity.id}/toggle`, {}, { headers });
      fetchActivities();
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في تحديث الحالة' : 'Failed to update status');
    }
  };

  // Package Functions
  // iter-249 (Feb 2026) — Deprecated in favour of navigating to the
  // dedicated `/control/company-setup/formation-packages/new` page. The
  // legacy modal state below is preserved only for the read-only listing
  // of legacy rakez_packages records; new packages live in
  // `formation_packages` and are edited via the unified form.
  const openAddPackageModal = () => {
    navigate('/control/company-setup/formation-packages/new?authority=rakez&back=/control/company-setup/rakez');
  };

  const openEditPackageModal = (pkg) => {
    // Legacy (rakez_packages collection) — no `authority` field, so we
    // detect legacy by absence and open a dedicated migration form.
    if (!pkg.authority) {
      toast.info(language === 'ar'
        ? 'هذه باقة قديمة — أعد إنشاءها بالفورم الجديد لتحصل على المخطط الكامل.'
        : 'This is a legacy package — recreate it via the new form for the full schema.');
      return;
    }
    navigate(`/control/company-setup/formation-packages/${pkg.id}/edit?back=/control/company-setup/rakez`);
  };

  const savePackage = async () => {
    // التحقق من صحة جميع الحقول
    if (Object.keys(validationErrors).length > 0) {
      toast.error(language === 'ar' ? 'يرجى تصحيح الأخطاء أولاً' : 'Please fix validation errors first');
      return;
    }

    if (!newPackage.name_ar || !newPackage.name_en) {
      toast.error(language === 'ar' ? 'يرجى إدخال اسم البكج' : 'Please enter package name');
      return;
    }

    if (newPackage.price_type === 'fixed' && !newPackage.price_fixed) {
      toast.error(language === 'ar' ? 'يرجى إدخال السعر' : 'Please enter price');
      return;
    }

    if (newPackage.price_type === 'range' && (!newPackage.price_min || !newPackage.price_max)) {
      toast.error(language === 'ar' ? 'يرجى إدخال نطاق السعر' : 'Please enter price range');
      return;
    }

    setSavingPackage(true);
    try {
      const packageData = {
        ...newPackage,
        activities_count: parseInt(newPackage.activities_count) || 1,
        visas_count: parseInt(newPackage.visas_count) || 0,
        price_fixed: newPackage.price_type === 'fixed' ? parseFloat(newPackage.price_fixed) : null,
        price_min: newPackage.price_type === 'range' ? parseFloat(newPackage.price_min) : null,
        price_max: newPackage.price_type === 'range' ? parseFloat(newPackage.price_max) : null,
      };

      if (editingPackage) {
        await axios.put(`${BACKEND_URL}/api/admin/rakez-packages/${editingPackage.id}`, packageData, { headers });
        toast.success(language === 'ar' ? 'تم تحديث البكج بنجاح' : 'Package updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/rakez-packages`, packageData, { headers });
        toast.success(language === 'ar' ? 'تم إضافة البكج بنجاح' : 'Package added successfully');
      }

      setShowPackageModal(false);
      fetchPackages();
    } catch (err) {
      const detail = err.response?.data?.detail;
      let errorMessage = language === 'ar' ? 'فشل في حفظ البكج' : 'Failed to save package';
      if (typeof detail === 'string') errorMessage = detail;
      else if (Array.isArray(detail)) errorMessage = detail.map(d => d.msg || d).join(', ');
      toast.error(errorMessage);
    } finally {
      setSavingPackage(false);
    }
  };

  const deletePackage = async (pkg) => {
    if (!confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا البكج؟' : 'Are you sure you want to delete this package?')) {
      return;
    }
    // iter-249 — Route delete to the correct backend based on the source
    const url = pkg._source === 'new'
      ? `${BACKEND_URL}/api/admin/formation-packages/${pkg.id}`
      : `${BACKEND_URL}/api/admin/rakez-packages/${pkg.id}`;
    try {
      await axios.delete(url, { headers });
      toast.success(language === 'ar' ? 'تم حذف البكج بنجاح' : 'Package deleted successfully');
      fetchPackages();
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في حذف البكج' : 'Failed to delete package');
    }
  };

  // Build breadcrumb items dynamically
  const getBreadcrumbItems = () => {
    const items = [
      { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/dashboard?view=cloudgm' },
      { labelAr: 'منصة العملاء', labelEn: 'Customers Platform', path: '/dashboard?view=cloudgm&platform=customers' },
      { labelAr: 'التحكم والمعلومات (منصة العملاء)', labelEn: 'Control & Info (Customers)', path: '/control/cloudgm' },
      { labelAr: 'تأسيس الشركات', labelEn: 'Company Setup', path: '/control/company-setup' },
      { labelAr: 'إمارة رأس الخيمة', labelEn: 'Ras Al Khaimah', path: '/control/company-setup/ras-al-khaimah' },
      { labelAr: 'راكز', labelEn: 'RAKEZ' }
    ];
    
    if (activeView === 'activities') {
      items.push({ labelAr: 'الأنشطة التجارية', labelEn: 'Business Activities' });
    } else if (activeView === 'packages') {
      items.push({ labelAr: 'البكجات', labelEn: 'Packages' });
    } else if (activeView === 'documents') {
      items.push({ labelAr: 'الأوراق المطلوبة', labelEn: 'Required Documents' });
      if (documentsInvestorType === 'inside_uae') {
        items.push({ labelAr: 'المستثمرين من داخل الدولة', labelEn: 'Inside UAE Investors' });
      } else if (documentsInvestorType === 'outside_uae') {
        items.push({ labelAr: 'المستثمرين من خارج الدولة', labelEn: 'Outside UAE Investors' });
      }
    }
    
    return items;
  };

  // Get subtitle based on active view
  const getSubtitle = () => {
    if (activeView === 'activities') return { ar: 'إدارة الأنشطة التجارية', en: 'Manage Business Activities' };
    if (activeView === 'packages') return { ar: 'إدارة باقات التأسيس', en: 'Manage Setup Packages' };
    if (activeView === 'documents') return { ar: 'إدارة الأوراق المطلوبة', en: 'Manage Required Documents' };
    return { ar: 'المنطقة الاقتصادية رأس الخيمة', en: 'Ras Al Khaimah Economic Zone' };
  };

  // Get back path based on active view
  const getBackPath = () => {
    if (activeView === 'main') return '/control/company-setup/ras-al-khaimah';
    if (activeView === 'documents' && documentsInvestorType !== null) return null; // Will use custom handler
    return null; // Will use custom handler
  };

  // Handle back navigation
  const handleBack = () => {
    if (activeView === 'main') {
      navigate('/control/company-setup/ras-al-khaimah');
    } else if (activeView === 'documents' && documentsInvestorType !== null) {
      setDocumentsInvestorType(null);
    } else {
      setActiveView('main');
    }
  };

  const subtitle = getSubtitle();

  const formatPrice = (pkg) => {
    // iter-249 — New FormationPackage records use `tenure_prices`
    // (list of {years, price}) — show the lowest tenure price.
    if (pkg._source === 'new') {
      const rows = pkg.tenure_prices || [];
      if (!rows.length) return '—';
      const min = Math.min(...rows.map((r) => Number(r.price) || 0));
      return min > 0 ? min.toLocaleString() : '—';
    }
    if (pkg.price_type === 'range') {
      const min = pkg.price_min || 0;
      const max = pkg.price_max || 0;
      return `${min.toLocaleString()} - ${max.toLocaleString()}`;
    }
    const fixed = pkg.price_fixed || 0;
    return fixed.toLocaleString();
  };

  return (
    <DashboardLayout
      title="راكز رأس الخيمة"
      titleEn="RAKEZ Ras Al Khaimah"
      subtitle={subtitle.ar}
      subtitleEn={subtitle.en}
      backPath={getBackPath()}
      onBack={!getBackPath() ? handleBack : undefined}
      breadcrumb={getBreadcrumbItems()}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main View - Three Buttons */}
        {activeView === 'main' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div
              onClick={() => setActiveView('activities')}
              className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-blue-500/50 transition-all duration-300 cursor-pointer hover:-translate-y-1 hover:shadow-lg"
              data-testid="rakez-activities-btn"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Briefcase className="h-10 w-10 text-blue-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                  {language === 'ar' ? 'الأنشطة التجارية' : 'Business Activities'}
                </h3>
                <p className="text-sm text-[#71717A]">
                  {language === 'ar' ? 'عرض وإدارة الأنشطة التجارية المتاحة' : 'View and manage available business activities'}
                </p>
              </div>
            </div>

            <div
              onClick={() => setActiveView('packages')}
              className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-amber-500/50 transition-all duration-300 cursor-pointer hover:-translate-y-1 hover:shadow-lg"
              data-testid="rakez-packages-btn"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-amber-500/10 border border-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Package className="h-10 w-10 text-amber-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">
                  {language === 'ar' ? 'البكجات' : 'Packages'}
                </h3>
                <p className="text-sm text-[#71717A]">
                  {language === 'ar' ? 'عرض وإدارة باقات التأسيس' : 'View and manage setup packages'}
                </p>
              </div>
            </div>

            <div
              onClick={() => setActiveView('documents')}
              className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-emerald-500/50 transition-all duration-300 cursor-pointer hover:-translate-y-1 hover:shadow-lg"
              data-testid="rakez-documents-btn"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <FileCheck className="h-10 w-10 text-emerald-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                  {language === 'ar' ? 'الأوراق المطلوبة' : 'Required Documents'}
                </h3>
                <p className="text-sm text-[#71717A]">
                  {language === 'ar' ? 'إدارة مستندات تأسيس الشركات' : 'Manage company setup documents'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Activities View */}
        {activeView === 'activities' && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Briefcase className="h-6 w-6 text-blue-500" />
                  {language === 'ar' ? 'الأنشطة التجارية - راكز' : 'RAKEZ Business Activities'}
                </h2>
                <p className="text-sm text-[#808080] mt-1">
                  {language === 'ar' ? `إجمالي ${totalActivities.toLocaleString()} نشاط` : `Total ${totalActivities.toLocaleString()} activities`}
                </p>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => navigate('/control/company-setup/ras-al-khaimah/rakez/statistics')}
                  variant="outline"
                  className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20 rounded-xl gap-2"
                  data-testid="statistics-btn"
                >
                  <BarChart3 className="h-4 w-4" />
                  {language === 'ar' ? 'الإحصائيات' : 'Statistics'}
                </Button>
                <Button
                  onClick={exportToPDF}
                  disabled={exportingPDF}
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/20 rounded-xl gap-2"
                  data-testid="export-pdf-btn"
                >
                  {exportingPDF ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                  PDF
                </Button>
                <Button
                  onClick={exportToExcel}
                  disabled={exportingExcel}
                  variant="outline"
                  className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20 rounded-xl gap-2"
                  data-testid="export-excel-btn"
                >
                  {exportingExcel ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Excel
                </Button>
                <Button
                  onClick={importFromExcel}
                  disabled={importingExcel}
                  variant="outline"
                  className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20 rounded-xl gap-2"
                  data-testid="import-excel-btn"
                >
                  {importingExcel ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="h-4 w-4" />
                  )}
                  {language === 'ar' ? 'تحديث' : 'Update'}
                </Button>
                <Button
                  onClick={openAddActivityModal}
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl gap-2"
                  data-testid="add-activity-btn"
                >
                  <Plus className="h-4 w-4" />
                  {language === 'ar' ? 'إضافة' : 'Add'}
                </Button>
              </div>
            </div>

            {/* Search & Filters — instant search (debounced) */}
            <div className="bg-[#1a1a2e] rounded-2xl p-4 border border-[#2a2a4e] mb-6">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px] relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#606060]" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={language === 'ar' ? 'ابدأ الكتابة للبحث بالاسم أو الكود...' : 'Start typing to search by name or code...'}
                    className="bg-[#0a0a0a] border-[#2a2a4e] text-white pr-10 rounded-xl"
                    data-testid="activity-search-input"
                  />
                </div>
                
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                  className="bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-4 py-2 min-w-[140px]"
                >
                  <option value="all">{language === 'ar' ? 'جميع الحالات' : 'All Status'}</option>
                  <option value="Active">{language === 'ar' ? 'نشط' : 'Active'}</option>
                  <option value="Inactive">{language === 'ar' ? 'غير نشط' : 'Inactive'}</option>
                </select>
                
                <select
                  value={licenseTypeFilter}
                  onChange={(e) => { setLicenseTypeFilter(e.target.value); setCurrentPage(1); }}
                  className="bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-4 py-2 min-w-[160px]"
                >
                  <option value="all">{language === 'ar' ? 'جميع الرخص' : 'All Licenses'}</option>
                  {availableLicenseTypes.map(lt => (
                    <option key={lt} value={lt}>{lt}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedActivities.length > 0 && (
              <div className="bg-[#1a1a2e] rounded-xl p-4 border border-blue-500/50 mb-4 flex items-center justify-between">
                <span className="text-sm text-white">
                  {language === 'ar' 
                    ? `تم تحديد ${selectedActivities.length} نشاط`
                    : `${selectedActivities.length} activities selected`
                  }
                </span>
                <div className="flex gap-2">
                  <Button
                    onClick={bulkActivate}
                    disabled={bulkActivating}
                    size="sm"
                    className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg gap-2"
                  >
                    {bulkActivating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    {language === 'ar' ? 'تفعيل المحدد' : 'Activate Selected'}
                  </Button>
                  <Button
                    onClick={bulkDeactivate}
                    disabled={bulkDeactivating}
                    size="sm"
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/20 rounded-lg gap-2"
                  >
                    {bulkDeactivating ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                    {language === 'ar' ? 'إلغاء تفعيل المحدد' : 'Deactivate Selected'}
                  </Button>
                </div>
              </div>
            )}

            {/* Activities Table */}
            {loadingActivities ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-16 text-[#808080] bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
                <Briefcase className="h-16 w-16 mx-auto mb-4 opacity-30 text-blue-500" />
                <p className="text-lg">{language === 'ar' ? 'لا توجد أنشطة' : 'No activities'}</p>
                <p className="text-sm mt-2 text-[#606060]">
                  {language === 'ar' ? 'اضغط على "تحديث" لاستيراد الأنشطة' : 'Click "Update" to import activities'}
                </p>
              </div>
            ) : (
              <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-2 p-4 bg-[#0a0a0a] border-b border-[#2a2a4e] text-xs font-semibold text-[#808080]">
                  <div className="col-span-1 flex items-center justify-center">
                    <button onClick={toggleSelectAll} className="p-1 hover:bg-[#2a2a4e] rounded">
                      {selectAll ? (
                        <CheckSquare className="h-4 w-4 text-blue-500" />
                      ) : (
                        <Square className="h-4 w-4 text-[#606060]" />
                      )}
                    </button>
                  </div>
                  <div className="col-span-1 text-center">#</div>
                  <div className="col-span-3">{language === 'ar' ? 'الاسم بالعربية' : 'Arabic Name'}</div>
                  <div className="col-span-3">{language === 'ar' ? 'الاسم بالإنجليزية' : 'English Name'}</div>
                  <div className="col-span-1 text-center">{language === 'ar' ? 'الكود' : 'Code'}</div>
                  <div className="col-span-1 text-center">{language === 'ar' ? 'الحالة' : 'Status'}</div>
                  <div className="col-span-2 text-center">{language === 'ar' ? 'إجراءات' : 'Actions'}</div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-[#2a2a4e] max-h-[500px] overflow-y-auto">
                  {activities.map((activity) => (
                    <div key={activity.id} className={`grid grid-cols-12 gap-2 p-4 items-center hover:bg-[#0a0a0a]/50 transition-colors ${
                      selectedActivities.includes(activity.id) ? 'bg-blue-500/10' : ''
                    }`}>
                      <div className="col-span-1 flex justify-center">
                        <button 
                          onClick={() => toggleSelectActivity(activity.id)} 
                          className="p-1 hover:bg-[#2a2a4e] rounded"
                        >
                          {selectedActivities.includes(activity.id) ? (
                            <CheckSquare className="h-4 w-4 text-blue-500" />
                          ) : (
                            <Square className="h-4 w-4 text-[#606060]" />
                          )}
                        </button>
                      </div>
                      <div className="col-span-1 text-center">
                        <span className="text-xs text-[#606060] font-mono">{activity.sequence}</span>
                      </div>
                      <div className="col-span-3">
                        <p className="text-sm font-medium text-white truncate" title={activity.activity_name_ar}>
                          {activity.activity_name_ar || '-'}
                        </p>
                      </div>
                      <div className="col-span-3">
                        <p className="text-sm text-[#a0a0a0] truncate" dir="ltr" title={activity.activity_name}>
                          {activity.activity_name || '-'}
                        </p>
                      </div>
                      <div className="col-span-1 text-center">
                        <span className="px-2 py-1 bg-[#0a0a0a] rounded-lg text-xs text-[#808080] font-mono">
                          {activity.activity_code || '-'}
                        </span>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                          activity.status === 'Active'
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {activity.status === 'Active' 
                            ? (language === 'ar' ? 'نشط' : 'Active')
                            : (language === 'ar' ? 'معطل' : 'Inactive')
                          }
                        </span>
                      </div>
                      <div className="col-span-2 flex justify-center gap-1">
                        <button
                          onClick={() => openDetailsModal(activity)}
                          className="w-7 h-7 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-blue-500/20 transition-colors"
                          title={language === 'ar' ? 'التفاصيل' : 'Details'}
                          data-testid={`details-btn-${activity.id}`}
                        >
                          <Eye className="h-3.5 w-3.5 text-blue-400" />
                        </button>
                        <button
                          onClick={() => setPrintTarget(activity)}
                          className="w-7 h-7 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-indigo-500/20 transition-colors"
                          title={language === 'ar' ? 'طباعة' : 'Print'}
                          data-testid={`print-btn-${activity.id}`}
                        >
                          <Printer className="h-3.5 w-3.5 text-indigo-400" />
                        </button>
                        <button
                          onClick={() => toggleActivityStatus(activity)}
                          className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                            activity.status === 'Active'
                              ? 'bg-emerald-500/20 hover:bg-emerald-500/30'
                              : 'bg-[#0a0a0a] hover:bg-[#2a2a4e]'
                          }`}
                          title={language === 'ar' ? 'تبديل الحالة' : 'Toggle Status'}
                        >
                          <Check className={`h-3.5 w-3.5 ${activity.status === 'Active' ? 'text-emerald-400' : 'text-[#606060]'}`} />
                        </button>
                        <button
                          onClick={() => openEditActivityModal(activity)}
                          className="w-7 h-7 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-amber-500/20 transition-colors"
                          title={language === 'ar' ? 'تعديل' : 'Edit'}
                        >
                          <Edit3 className="h-3.5 w-3.5 text-[#808080] hover:text-amber-400" />
                        </button>
                        <button
                          onClick={() => deleteActivity(activity.id)}
                          className="w-7 h-7 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-red-500/20 transition-colors"
                          title={language === 'ar' ? 'حذف' : 'Delete'}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-[#808080] hover:text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Enhanced Pagination */}
                <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-t border-[#2a2a4e]">
                  <p className="text-sm text-[#808080]">
                    {language === 'ar' 
                      ? `صفحة ${currentPage} من ${totalPages} (${totalActivities.toLocaleString()} نشاط)` 
                      : `Page ${currentPage} of ${totalPages} (${totalActivities.toLocaleString()} activities)`
                    }
                  </p>
                  
                  <div className="flex items-center gap-2">
                    {/* First Page */}
                    <Button
                      onClick={() => goToPage(1)}
                      disabled={currentPage <= 1}
                      variant="outline"
                      size="sm"
                      className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] w-8 h-8 p-0"
                      title={language === 'ar' ? 'الصفحة الأولى' : 'First Page'}
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                    
                    {/* Previous Page */}
                    <Button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage <= 1}
                      variant="outline"
                      size="sm"
                      className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] w-8 h-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    
                    {/* Go to Page Input */}
                    <form onSubmit={handleGoToPageSubmit} className="flex items-center gap-1">
                      <Input
                        type="number"
                        min="1"
                        max={totalPages}
                        value={goToPageInput}
                        onChange={(e) => setGoToPageInput(e.target.value)}
                        placeholder={currentPage.toString()}
                        className="w-16 h-8 text-center bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-lg text-sm"
                      />
                      <Button
                        type="submit"
                        variant="outline"
                        size="sm"
                        className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] h-8 px-2"
                      >
                        {language === 'ar' ? 'انتقل' : 'Go'}
                      </Button>
                    </form>
                    
                    {/* Next Page */}
                    <Button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      variant="outline"
                      size="sm"
                      className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] w-8 h-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    {/* Last Page */}
                    <Button
                      onClick={() => goToPage(totalPages)}
                      disabled={currentPage >= totalPages}
                      variant="outline"
                      size="sm"
                      className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] w-8 h-8 p-0"
                      title={language === 'ar' ? 'الصفحة الأخيرة' : 'Last Page'}
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Packages View */}
        {activeView === 'packages' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Package className="h-6 w-6 text-amber-500" />
                {language === 'ar' ? 'البكجات - راكز' : 'RAKEZ Packages'}
              </h2>
              
              <Button
                onClick={openAddPackageModal}
                className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl gap-2"
                data-testid="add-package-btn"
              >
                <Plus className="h-4 w-4" />
                {language === 'ar' ? 'إضافة بكج' : 'Add Package'}
              </Button>
            </div>

            {loadingPackages ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
              </div>
            ) : packages.length === 0 ? (
              <div className="text-center py-16 text-[#808080] bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
                <Package className="h-16 w-16 mx-auto mb-4 opacity-30 text-amber-500" />
                <p className="text-lg">{language === 'ar' ? 'لا توجد بكجات بعد' : 'No packages yet'}</p>
                <p className="text-sm mt-2 text-[#606060]">
                  {language === 'ar' ? 'اضغط على "إضافة بكج" لإنشاء بكج جديد' : 'Click "Add Package" to create a new package'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {packages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className={`bg-[#1a1a2e] rounded-2xl p-6 border transition-all ${
                      pkg.is_active ? 'border-[#2a2a4e]' : 'border-red-500/30 opacity-60'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                          <Package className="h-6 w-6 text-amber-500" />
                        </div>
                        <div>
                          <h3 className="font-bold text-white">
                            {language === 'ar' ? pkg.name_ar : pkg.name_en}
                          </h3>
                          <div className="flex items-center gap-1 mt-0.5">
                            {pkg._source === 'new' ? (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">
                                {language === 'ar' ? 'مخطط موحّد' : 'Unified'}
                              </span>
                            ) : (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-white/60 border border-white/10">
                                {language === 'ar' ? 'قديم' : 'Legacy'}
                              </span>
                            )}
                            {pkg._source !== 'new' && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                pkg.price_type === 'fixed'
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : 'bg-orange-500/20 text-orange-400'
                              }`}>
                                {pkg.price_type === 'fixed'
                                  ? (language === 'ar' ? 'سعر ثابت' : 'Fixed')
                                  : (language === 'ar' ? 'نطاق سعري' : 'Range')
                                }
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openEditPackageModal(pkg)}
                          className="w-8 h-8 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-blue-500/20 transition-colors"
                        >
                          <Edit3 className="h-4 w-4 text-[#808080] hover:text-blue-400" />
                        </button>
                        <button
                          onClick={() => deletePackage(pkg)}
                          className="w-8 h-8 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-red-500/20 transition-colors"
                        >
                          <Trash2 className="h-4 w-4 text-[#808080] hover:text-red-400" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#808080] flex items-center gap-2">
                          <Hash className="h-4 w-4" />
                          {language === 'ar' ? 'عدد الأنشطة' : 'Activities'}
                        </span>
                        <span className="text-white font-medium">
                          {pkg._source === 'new' ? (pkg.max_activities ?? '—') : (pkg.activities_count ?? '—')}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#808080] flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          {language === 'ar' ? 'عدد الإقامات' : 'Visas'}
                        </span>
                        <span className="text-white font-medium">
                          {pkg._source === 'new' ? (pkg.included_visas ?? '—') : (pkg.visas_count ?? '—')}
                        </span>
                      </div>
                      <div className="pt-3 border-t border-[#2a2a4e]">
                        <div className="flex items-center justify-between">
                          <span className="text-[#808080] flex items-center gap-2">
                            <DollarSign className="h-4 w-4" />
                            {language === 'ar' ? 'السعر' : 'Price'}
                          </span>
                          <span className="text-amber-500 font-bold text-lg flex items-center gap-1">
                            <DirhamPrice value={Number(formatPrice(pkg).replace(/[^0-9.]/g, '')) || 0} decimals={0} size="sm" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Required Documents View */}
        {activeView === 'documents' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <FileCheck className="h-6 w-6 text-emerald-500" />
                {language === 'ar' ? 'الأوراق المطلوبة للتأسيس - راكز' : 'RAKEZ Required Documents'}
              </h2>
            </div>

            {loadingDocuments ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
              </div>
            ) : documentsInvestorType === null ? (
              /* Main Selection - Two Buttons */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {/* زر المستثمرين من خارج الدولة */}
                <div
                  onClick={() => {
                    setDocumentsInvestorType('outside_uae');
                    fetchMasterDocuments();
                    fetchEnabledDocumentsForInvestor('outside_uae');
                  }}
                  className="group bg-[#1a1a2e] rounded-2xl p-8 border border-[#2a2a4e] hover:border-blue-500 transition-all duration-300 cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-500/20"
                  data-testid="docs-outside-btn"
                >
                  <div className="text-center">
                    <div className="w-24 h-24 bg-gradient-to-br from-blue-500/20 to-blue-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform border border-blue-500/30">
                      <Globe className="h-12 w-12 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      {language === 'ar' ? 'المستثمرين من خارج الدولة' : 'Investors from Outside UAE'}
                    </h3>
                    <p className="text-sm text-[#808080] mb-4">
                      {language === 'ar' ? 'إدارة مستندات المستثمرين الأجانب' : 'Manage foreign investor documents'}
                    </p>
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                      documentsConfig.outside_uae?.is_active 
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {documentsConfig.outside_uae?.is_active 
                        ? (language === 'ar' ? 'فعال' : 'Active')
                        : (language === 'ar' ? 'غير فعال' : 'Inactive')}
                    </span>
                  </div>
                </div>

                {/* زر المستثمرين من داخل الدولة */}
                <div
                  onClick={() => {
                    setDocumentsInvestorType('inside_uae');
                    fetchMasterDocuments();
                    fetchEnabledDocumentsForInvestor('inside_uae');
                  }}
                  className="group bg-[#1a1a2e] rounded-2xl p-8 border border-[#2a2a4e] hover:border-emerald-500 transition-all duration-300 cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:shadow-emerald-500/20"
                  data-testid="docs-inside-btn"
                >
                  <div className="text-center">
                    <div className="w-24 h-24 bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform border border-emerald-500/30">
                      <Building2 className="h-12 w-12 text-emerald-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      {language === 'ar' ? 'المستثمرين من داخل الدولة' : 'Investors from Inside UAE'}
                    </h3>
                    <p className="text-sm text-[#808080] mb-4">
                      {language === 'ar' ? 'إدارة مستندات المقيمين في الإمارات' : 'Manage UAE resident documents'}
                    </p>
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                      documentsConfig.inside_uae?.is_active 
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {documentsConfig.inside_uae?.is_active 
                        ? (language === 'ar' ? 'فعال' : 'Active')
                        : (language === 'ar' ? 'غير فعال' : 'Inactive')}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              /* Documents Detail View */
              <div className="max-w-4xl mx-auto">
                <div className={`bg-[#1a1a2e] rounded-2xl border transition-all ${
                  documentsConfig[documentsInvestorType]?.is_active ? 'border-[#2a2a4e]' : 'border-red-500/30'
                }`}>
                  {/* Header */}
                  <div className="flex items-center justify-between p-5 border-b border-[#2a2a4e]">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        documentsInvestorType === 'outside_uae' ? 'bg-blue-500/20' : 'bg-emerald-500/20'
                      }`}>
                        {documentsInvestorType === 'outside_uae' 
                          ? <Globe className="h-6 w-6 text-blue-500" />
                          : <Building2 className="h-6 w-6 text-emerald-500" />
                        }
                      </div>
                      <div>
                        <h3 className="font-bold text-white">
                          {documentsInvestorType === 'outside_uae' 
                            ? (language === 'ar' ? 'المستثمرين من خارج الدولة' : 'Investors from Outside UAE')
                            : (language === 'ar' ? 'المستثمرين من داخل الدولة' : 'Investors from Inside UAE')
                          }
                        </h3>
                        <p className="text-sm text-[#808080]">
                          {documentsInvestorType === 'outside_uae'
                            ? (language === 'ar' ? 'المستندات المطلوبة للمستثمرين الأجانب' : 'Documents required for foreign investors')
                            : (language === 'ar' ? 'المستندات المطلوبة للمقيمين في الإمارات' : 'Documents required for UAE residents')
                          }
                        </p>
                      </div>
                    </div>
                    
                    {/* زر التفعيل الرئيسي */}
                    <button
                      onClick={() => toggleDocumentsStatus(documentsInvestorType)}
                      className={`relative w-16 h-8 rounded-full transition-all duration-300 ${
                        documentsConfig[documentsInvestorType]?.is_active 
                          ? 'bg-emerald-500'
                          : 'bg-red-500/50'
                      }`}
                      data-testid="toggle-main-status"
                    >
                      <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all duration-300 shadow-md ${
                        documentsConfig[documentsInvestorType]?.is_active ? 'right-1' : 'left-1'
                      }`} />
                    </button>
                  </div>

                  {/* Documents List from Master Documents */}
                  <div className={`p-5 space-y-3 transition-opacity ${
                    !documentsConfig[documentsInvestorType]?.is_active ? 'opacity-50 pointer-events-none' : ''
                  }`}>
                    {masterDocuments.length === 0 ? (
                      <div className="text-center py-8 text-[#808080]">
                        <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>{language === 'ar' ? 'لا توجد أوراق في القائمة المركزية' : 'No documents in master list'}</p>
                        <button
                          onClick={() => navigate('/control/cloudgm/master-documents')}
                          className="mt-3 text-[#c9a227] hover:text-[#f5d742] text-sm underline"
                        >
                          {language === 'ar' ? 'إدارة قائمة الأوراق' : 'Manage Documents List'}
                        </button>
                      </div>
                    ) : (
                      <>
                        {masterDocuments.map((doc) => (
                          <div
                            key={doc.id}
                            onClick={() => toggleDocumentEnabled(doc.id)}
                            className={`p-4 bg-[#0a0a0a] rounded-xl flex items-center justify-between cursor-pointer transition-colors ${
                              enabledDocuments.includes(doc.id) ? 'border border-emerald-500/50' : 'border border-transparent hover:border-[#2a2a4e]'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <FileText className="h-5 w-5 text-[#c9a227]" />
                              <span className="text-white">
                                {language === 'ar' ? doc.name_ar : doc.name_en}
                              </span>
                            </div>
                            <button
                              className={`w-12 h-7 rounded-full transition-colors relative ${
                                enabledDocuments.includes(doc.id) ? 'bg-emerald-500' : 'bg-[#2a2a4e]'
                              }`}
                            >
                              <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all duration-300 shadow-md ${
                                enabledDocuments.includes(doc.id) ? 'right-1' : 'left-1'
                              }`} />
                            </button>
                          </div>
                        ))}
                        
                        {/* خيار الهوية الصادرة من دولة المقيم */}
                        {residentIdOption.enabled_globally && (
                          <div className="mt-4 border-t border-[#2a2a4e] pt-4">
                            <div
                              onClick={() => setResidentIdOption(prev => ({
                                ...prev,
                                enabled_for_service: !prev.enabled_for_service
                              }))}
                              className={`p-4 bg-[#0a0a0a] rounded-xl cursor-pointer transition-colors ${
                                residentIdOption.enabled_for_service ? 'border border-violet-500/50' : 'border border-transparent hover:border-[#2a2a4e]'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <Globe className="h-5 w-5 text-violet-400" />
                                  <div>
                                    <span className="text-white font-medium">
                                      {language === 'ar' ? 'الهوية الصادرة من دولة المقيم' : 'Resident Country ID'}
                                    </span>
                                    <p className="text-xs text-[#808080] mt-1">
                                      {language === 'ar' 
                                        ? 'حدد الجنسيات المطلوب منها تقديم هوية من دولة إقامتهم'
                                        : 'Select nationalities required to provide ID from their residence country'}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  className={`w-12 h-7 rounded-full transition-colors relative ${
                                    residentIdOption.enabled_for_service ? 'bg-violet-500' : 'bg-[#2a2a4e]'
                                  }`}
                                >
                                  <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all duration-300 shadow-md ${
                                    residentIdOption.enabled_for_service ? 'right-1' : 'left-1'
                                  }`} />
                                </button>
                              </div>
                            </div>
                            
                            {/* قائمة اختيار الجنسيات */}
                            {residentIdOption.enabled_for_service && (
                              <div className="mt-3 p-4 bg-[#0a0a0a] rounded-xl border border-violet-500/20">
                                {/* شريط البحث */}
                                <div className="relative mb-3">
                                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#808080]" />
                                  <input
                                    type="text"
                                    value={nationalitySearch}
                                    onChange={(e) => setNationalitySearch(e.target.value)}
                                    placeholder={language === 'ar' ? 'ابحث عن جنسية...' : 'Search nationality...'}
                                    className="w-full bg-[#1a1a2e] text-white placeholder:text-[#808080] rounded-lg py-2 px-10 text-sm border border-[#2a2a4e] focus:outline-none focus:border-violet-500/50"
                                    dir={language === 'ar' ? 'rtl' : 'ltr'}
                                  />
                                </div>
                                
                                {/* الجنسيات المحددة */}
                                {residentIdOption.nationalities.length > 0 && (
                                  <div className="flex flex-wrap gap-2 mb-3">
                                    {residentIdOption.nationalities.map(code => {
                                      const nat = allNationalities.find(n => n.code === code);
                                      if (!nat) return null;
                                      return (
                                        <span 
                                          key={code}
                                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-violet-500/20 text-violet-300 text-xs"
                                        >
                                          {language === 'ar' ? nat.name_ar : nat.name_en}
                                          <button 
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setResidentIdOption(prev => ({
                                                ...prev,
                                                nationalities: prev.nationalities.filter(c => c !== code)
                                              }));
                                            }}
                                            className="hover:text-white"
                                          >
                                            <X className="h-3 w-3" />
                                          </button>
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                                
                                {/* قائمة الجنسيات */}
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                                  {allNationalities
                                    .filter(nat => {
                                      if (!nationalitySearch) return true;
                                      const search = nationalitySearch.toLowerCase();
                                      return nat.name_ar.includes(nationalitySearch) || 
                                             nat.name_en.toLowerCase().includes(search);
                                    })
                                    .map(nat => (
                                      <button
                                        key={nat.code}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setResidentIdOption(prev => ({
                                            ...prev,
                                            nationalities: prev.nationalities.includes(nat.code)
                                              ? prev.nationalities.filter(c => c !== nat.code)
                                              : [...prev.nationalities, nat.code]
                                          }));
                                        }}
                                        className={`flex items-center gap-2 p-2 rounded-lg text-xs text-start transition-colors ${
                                          residentIdOption.nationalities.includes(nat.code)
                                            ? 'bg-violet-500/30 text-violet-200'
                                            : 'bg-[#1a1a2e] text-[#A1A1AA] hover:bg-[#2a2a4e]'
                                        }`}
                                      >
                                        <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                          residentIdOption.nationalities.includes(nat.code)
                                            ? 'bg-violet-500 border-violet-500'
                                            : 'border-[#808080]'
                                        }`}>
                                          {residentIdOption.nationalities.includes(nat.code) && (
                                            <Check className="h-3 w-3 text-white" />
                                          )}
                                        </div>
                                        <span className="truncate">
                                          {language === 'ar' ? nat.name_ar : nat.name_en}
                                        </span>
                                      </button>
                                    ))
                                  }
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Save Button */}
                        <div className="pt-4 flex justify-end">
                          <button
                            onClick={() => saveEnabledDocuments(documentsInvestorType)}
                            disabled={savingDocuments}
                            className="px-6 py-2 bg-gradient-to-r from-[#c9a227] to-[#e3b930] hover:from-[#d4af37] hover:to-[#f0c040] text-black font-medium rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
                            data-testid="save-documents-btn"
                          >
                            {savingDocuments ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4" />
                            )}
                            {language === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

      {/* Activity Details Modal */}
      {showDetailsModal && selectedActivity && (
        <div className="fixed inset-0 bg-black/80 z-[100] overflow-y-auto p-4 flex items-start sm:items-center justify-center">
          <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] w-full max-w-2xl my-6 sm:my-8 relative">
            <div className="flex items-center justify-between p-6 border-b border-[#2a2a4e] sticky top-0 bg-[#1a1a2e] z-10 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <Eye className="h-5 w-5 text-blue-500" />
                </div>
                <h2 className="text-lg font-bold text-white">
                  {language === 'ar' ? 'تفاصيل النشاط' : 'Activity Details'}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPrintTarget(selectedActivity)}
                  className="w-8 h-8 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-blue-500/20 transition-colors"
                  title={language === 'ar' ? 'طباعة النشاط' : 'Print activity'}
                  data-testid="details-print-btn"
                >
                  <Printer className="h-4 w-4 text-blue-400" />
                </button>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="w-8 h-8 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-red-500/20 transition-colors"
                >
                  <X className="h-4 w-4 text-[#808080]" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <DetailItem label={language === 'ar' ? 'الرقم التسلسلي' : 'Sequence'} value={selectedActivity.sequence} />
                <DetailItem label={language === 'ar' ? 'الكود' : 'Code'} value={selectedActivity.activity_code} />
                <DetailItem label={language === 'ar' ? 'الاسم بالعربية' : 'Arabic Name'} value={selectedActivity.activity_name_ar} fullWidth />
                <DetailItem label={language === 'ar' ? 'الاسم بالإنجليزية' : 'English Name'} value={selectedActivity.activity_name} fullWidth dir="ltr" />
              </div>

              <div className="border-t border-[#2a2a4e] pt-4">
                <h3 className="text-sm font-semibold text-white mb-3">{language === 'ar' ? 'معلومات الرخصة' : 'License Info'}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DetailItem label={language === 'ar' ? 'نوع الرخصة' : 'License Type'} value={selectedActivity.license_type} />
                  <DetailItem label={language === 'ar' ? 'نوع الرخصة (عربي)' : 'License Type (AR)'} value={selectedActivity.license_type_ar} />
                  <DetailItem label={language === 'ar' ? 'المنطقة' : 'Zone'} value={selectedActivity.zone} />
                  <DetailItem label={language === 'ar' ? 'الحالة' : 'Status'} value={selectedActivity.status} highlight={selectedActivity.status === 'Active' ? 'green' : 'red'} />
                </div>
              </div>

              <div className="border-t border-[#2a2a4e] pt-4">
                <h3 className="text-sm font-semibold text-white mb-3">{language === 'ar' ? 'التصنيف' : 'Classification'}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DetailItem label={language === 'ar' ? 'مجموعة النشاط' : 'Activity Group'} value={selectedActivity.activity_group} />
                  <DetailItem label={language === 'ar' ? 'مجموعة النشاط (عربي)' : 'Activity Group (AR)'} value={selectedActivity.activity_group_ar} />
                  <DetailItem label={language === 'ar' ? 'قطاع GICS' : 'GICS Sector'} value={selectedActivity.gics_business_sector} />
                  <DetailItem label={language === 'ar' ? 'القطاع الفرعي' : 'Subsector'} value={selectedActivity.gics_subsector} />
                </div>
              </div>

              <div className="border-t border-[#2a2a4e] pt-4">
                <h3 className="text-sm font-semibold text-white mb-3">{language === 'ar' ? 'الوصف' : 'Description'}</h3>
                <DetailItem label={language === 'ar' ? 'الوصف بالإنجليزية' : 'English Description'} value={selectedActivity.description_en} fullWidth dir="ltr" />
                <DetailItem label={language === 'ar' ? 'الوصف بالعربية' : 'Arabic Description'} value={selectedActivity.description_ar} fullWidth />
              </div>

              <div className="border-t border-[#2a2a4e] pt-4">
                <h3 className="text-sm font-semibold text-white mb-3">{language === 'ar' ? 'متطلبات' : 'Requirements'}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DetailItem label={language === 'ar' ? 'رأس المال الأدنى' : 'Min Share Capital'} value={selectedActivity.minimum_share_capital || '-'} />
                  <DetailItem label={language === 'ar' ? 'السعر' : 'Price'} value={selectedActivity.activity_price || '-'} />
                </div>
                <DetailItem label={language === 'ar' ? 'اشتراطات النشاط' : 'Activity Requirements'} value={selectedActivity.qualification_requirement} fullWidth />
                <DetailItem label={language === 'ar' ? 'المستندات المطلوبة' : 'Required Documents'} value={selectedActivity.documents_required} fullWidth />
              </div>

              <div className="border-t border-[#2a2a4e] pt-4">
                <h3 className="text-sm font-semibold text-white mb-3">{language === 'ar' ? 'معلومات إضافية' : 'Additional Info'}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DetailItem label={language === 'ar' ? 'تصنيف المخاطر HSE' : 'HSE Risk Classification'} value={selectedActivity.rakez_hse_risk} />
                  <DetailItem label={language === 'ar' ? 'تقييم مخاطر الامتثال' : 'Compliance Risk Rating'} value={selectedActivity.compliance_risk_rating} />
                  <DetailItem label={language === 'ar' ? 'نشاط خاص' : 'Is Special'} value={selectedActivity.is_special} />
                  <DetailItem label={language === 'ar' ? 'غير مسموح للعمل المشترك' : 'Not Allowed Coworking'} value={selectedActivity.is_not_allowed_coworking} />
                </div>
              </div>
            </div>

            <div className="flex justify-end p-6 border-t border-[#2a2a4e]">
              <Button
                onClick={() => setShowDetailsModal(false)}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                {language === 'ar' ? 'إغلاق' : 'Close'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Activity Modal */}
      {showActivityModal && (
        <div className="fixed inset-0 bg-black/70 z-[100] overflow-y-auto p-4 flex items-start sm:items-center justify-center">
          <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] w-full max-w-3xl my-6 sm:my-8">
            <div className="flex items-center justify-between p-6 border-b border-[#2a2a4e] sticky top-0 bg-[#1a1a2e] z-10 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <Briefcase className="h-5 w-5 text-blue-500" />
                </div>
                <h2 className="text-lg font-bold text-white">
                  {editingActivity 
                    ? (language === 'ar' ? 'تعديل النشاط' : 'Edit Activity')
                    : (language === 'ar' ? 'إضافة نشاط جديد' : 'Add New Activity')
                  }
                </h2>
              </div>
              <button
                onClick={() => setShowActivityModal(false)}
                className="w-8 h-8 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-red-500/20 transition-colors"
                data-testid="close-activity-modal-btn"
              >
                <X className="h-4 w-4 text-[#808080]" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* ── Basic ─────────────────────────────────── */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3 pb-2 border-b border-[#2a2a4e]">
                  {language === 'ar' ? 'المعلومات الأساسية' : 'Basic Info'}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'اسم النشاط بالعربية' : 'Activity Name (Arabic)'} *
                    </label>
                    <Input
                      value={newActivity.name_ar}
                      onChange={(e) => setNewActivity({ ...newActivity, name_ar: e.target.value })}
                      placeholder={language === 'ar' ? 'مثال: تجارة المواد الغذائية' : 'مثال: تجارة المواد الغذائية'}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                      data-testid="activity-name-ar-input"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'اسم النشاط بالإنجليزية' : 'Activity Name (English)'} *
                    </label>
                    <Input
                      value={newActivity.name_en}
                      onChange={(e) => setNewActivity({ ...newActivity, name_en: e.target.value })}
                      placeholder="Example: Food Trading"
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                      dir="ltr"
                      data-testid="activity-name-en-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'كود النشاط' : 'Activity Code'}
                    </label>
                    <Input
                      value={newActivity.code}
                      onChange={(e) => setNewActivity({ ...newActivity, code: e.target.value })}
                      placeholder="e.g. 3020002"
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                      dir="ltr"
                      data-testid="activity-code-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'الحالة' : 'Status'}
                    </label>
                    <select
                      value={newActivity.is_active ? 'active' : 'inactive'}
                      onChange={(e) => setNewActivity({ ...newActivity, is_active: e.target.value === 'active' })}
                      className="w-full bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-4 py-2"
                      data-testid="activity-status-select"
                    >
                      <option value="active">{language === 'ar' ? 'نشط' : 'Active'}</option>
                      <option value="inactive">{language === 'ar' ? 'غير نشط' : 'Inactive'}</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* ── License ─────────────────────────────────── */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3 pb-2 border-b border-[#2a2a4e]">
                  {language === 'ar' ? 'معلومات الرخصة' : 'License Info'}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'نوع الرخصة (إنجليزي)' : 'License Type (EN)'}
                    </label>
                    <Input
                      value={newActivity.license_type}
                      onChange={(e) => setNewActivity({ ...newActivity, license_type: e.target.value })}
                      placeholder="Industrial"
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                      dir="ltr"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'نوع الرخصة (عربي)' : 'License Type (AR)'}
                    </label>
                    <Input
                      value={newActivity.license_type_ar}
                      onChange={(e) => setNewActivity({ ...newActivity, license_type_ar: e.target.value })}
                      placeholder="صناعية"
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'المنطقة' : 'Zone'}
                    </label>
                    <Input
                      value={newActivity.zone}
                      onChange={(e) => setNewActivity({ ...newActivity, zone: e.target.value })}
                      placeholder="Non-Freezone / Freezone"
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                      dir="ltr"
                    />
                  </div>
                </div>
              </div>

              {/* ── Classification ─────────────────────────── */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3 pb-2 border-b border-[#2a2a4e]">
                  {language === 'ar' ? 'التصنيف' : 'Classification'}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'مجموعة النشاط (إنجليزي)' : 'Activity Group (EN)'}
                    </label>
                    <Input
                      value={newActivity.activity_group}
                      onChange={(e) => setNewActivity({ ...newActivity, activity_group: e.target.value })}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                      dir="ltr"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'مجموعة النشاط (عربي)' : 'Activity Group (AR)'}
                    </label>
                    <Input
                      value={newActivity.activity_group_ar}
                      onChange={(e) => setNewActivity({ ...newActivity, activity_group_ar: e.target.value })}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'قطاع GICS' : 'GICS Sector'}
                    </label>
                    <Input
                      value={newActivity.gics_business_sector}
                      onChange={(e) => setNewActivity({ ...newActivity, gics_business_sector: e.target.value })}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                      dir="ltr"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'القطاع الفرعي' : 'Subsector'}
                    </label>
                    <Input
                      value={newActivity.gics_subsector}
                      onChange={(e) => setNewActivity({ ...newActivity, gics_subsector: e.target.value })}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                      dir="ltr"
                    />
                  </div>
                </div>
              </div>

              {/* ── Description ─────────────────────────────── */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3 pb-2 border-b border-[#2a2a4e]">
                  {language === 'ar' ? 'الوصف' : 'Description'}
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'الوصف بالعربية' : 'Arabic Description'}
                    </label>
                    <textarea
                      value={newActivity.description_ar}
                      onChange={(e) => setNewActivity({ ...newActivity, description_ar: e.target.value })}
                      rows={3}
                      className="w-full bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-4 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'الوصف بالإنجليزية' : 'English Description'}
                    </label>
                    <textarea
                      value={newActivity.description_en}
                      onChange={(e) => setNewActivity({ ...newActivity, description_en: e.target.value })}
                      rows={3}
                      className="w-full bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-4 py-2"
                      dir="ltr"
                    />
                  </div>
                </div>
              </div>

              {/* ── Requirements ─────────────────────────────── */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3 pb-2 border-b border-[#2a2a4e]">
                  {language === 'ar' ? 'الاشتراطات' : 'Requirements'}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'رأس المال الأدنى' : 'Min Share Capital'}
                    </label>
                    <Input
                      value={newActivity.minimum_share_capital}
                      onChange={(e) => setNewActivity({ ...newActivity, minimum_share_capital: e.target.value })}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                      dir="ltr"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'السعر' : 'Price'}
                    </label>
                    <Input
                      value={newActivity.activity_price}
                      onChange={(e) => setNewActivity({ ...newActivity, activity_price: e.target.value })}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                      dir="ltr"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'اشتراطات النشاط' : 'Activity Requirements'}
                    </label>
                    <textarea
                      value={newActivity.qualification_requirement}
                      onChange={(e) => setNewActivity({ ...newActivity, qualification_requirement: e.target.value })}
                      rows={3}
                      className="w-full bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-4 py-2"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'المستندات المطلوبة' : 'Required Documents'}
                    </label>
                    <textarea
                      value={newActivity.documents_required}
                      onChange={(e) => setNewActivity({ ...newActivity, documents_required: e.target.value })}
                      rows={3}
                      className="w-full bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-4 py-2"
                    />
                  </div>
                </div>
              </div>

              {/* ── Additional ─────────────────────────────── */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3 pb-2 border-b border-[#2a2a4e]">
                  {language === 'ar' ? 'معلومات إضافية' : 'Additional Info'}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'تصنيف المخاطر HSE' : 'HSE Risk'}
                    </label>
                    <Input
                      value={newActivity.rakez_hse_risk}
                      onChange={(e) => setNewActivity({ ...newActivity, rakez_hse_risk: e.target.value })}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                      dir="ltr"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'تقييم مخاطر الامتثال' : 'Compliance Risk'}
                    </label>
                    <Input
                      value={newActivity.compliance_risk_rating}
                      onChange={(e) => setNewActivity({ ...newActivity, compliance_risk_rating: e.target.value })}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                      dir="ltr"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'نشاط خاص' : 'Special Activity'}
                    </label>
                    <select
                      value={newActivity.is_special || ''}
                      onChange={(e) => setNewActivity({ ...newActivity, is_special: e.target.value })}
                      className="w-full bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-4 py-2"
                    >
                      <option value="">-</option>
                      <option value="Yes">{language === 'ar' ? 'نعم' : 'Yes'}</option>
                      <option value="No">{language === 'ar' ? 'لا' : 'No'}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'غير مسموح للعمل المشترك' : 'Not Allowed Coworking'}
                    </label>
                    <select
                      value={newActivity.is_not_allowed_coworking || ''}
                      onChange={(e) => setNewActivity({ ...newActivity, is_not_allowed_coworking: e.target.value })}
                      className="w-full bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-4 py-2"
                    >
                      <option value="">-</option>
                      <option value="Yes">{language === 'ar' ? 'نعم' : 'Yes'}</option>
                      <option value="No">{language === 'ar' ? 'لا' : 'No'}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-[#2a2a4e] sticky bottom-0 bg-[#1a1a2e] rounded-b-2xl">
              <Button
                onClick={() => setShowActivityModal(false)}
                variant="outline"
                className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e]"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={saveActivity}
                disabled={savingActivity}
                className="bg-blue-500 hover:bg-blue-600 text-white gap-2"
                data-testid="save-activity-btn"
              >
                {savingActivity ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {editingActivity 
                  ? (language === 'ar' ? 'تحديث' : 'Update')
                  : (language === 'ar' ? 'إضافة' : 'Add')
                }
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Package Modal */}
      {showPackageModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] w-full max-w-lg my-8">
            <div className="flex items-center justify-between p-6 border-b border-[#2a2a4e]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <Package className="h-5 w-5 text-amber-500" />
                </div>
                <h2 className="text-lg font-bold text-white">
                  {editingPackage 
                    ? (language === 'ar' ? 'تعديل البكج' : 'Edit Package')
                    : (language === 'ar' ? 'إضافة بكج جديد' : 'Add New Package')
                  }
                </h2>
              </div>
              <button
                onClick={() => setShowPackageModal(false)}
                className="w-8 h-8 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-red-500/20 transition-colors"
              >
                <X className="h-4 w-4 text-[#808080]" />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* اسم البكج بالعربية */}
              <div>
                <label className="block text-sm text-[#808080] mb-2">
                  {language === 'ar' ? 'اسم البكج بالعربية' : 'Package Name (Arabic)'} *
                </label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      value={newPackage.name_ar}
                      onChange={(e) => updatePackageField('name_ar', e.target.value)}
                      placeholder="مثال: بكج البداية"
                      className={`bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl ${validationErrors.name_ar ? 'border-red-500' : ''}`}
                    />
                    {validationErrors.name_ar && (
                      <p className="text-xs text-red-400 mt-1">{validationErrors.name_ar}</p>
                    )}
                  </div>
                  <Button
                    type="button"
                    onClick={() => translateText(newPackage.name_ar, 'ar', 'en', 'name_en')}
                    disabled={!newPackage.name_ar || translatingField === 'name_en'}
                    variant="outline"
                    size="sm"
                    className="border-[#2a2a4e] text-blue-400 hover:bg-blue-500/20 w-10 h-10 p-0"
                    title={language === 'ar' ? 'ترجمة إلى الإنجليزية' : 'Translate to English'}
                  >
                    {translatingField === 'name_en' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Languages className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* اسم البكج بالإنجليزية */}
              <div>
                <label className="block text-sm text-[#808080] mb-2">
                  {language === 'ar' ? 'اسم البكج بالإنجليزية' : 'Package Name (English)'} *
                </label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      value={newPackage.name_en}
                      onChange={(e) => updatePackageField('name_en', e.target.value)}
                      placeholder="Example: Starter Package"
                      className={`bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl ${validationErrors.name_en ? 'border-red-500' : ''}`}
                      dir="ltr"
                    />
                    {validationErrors.name_en && (
                      <p className="text-xs text-red-400 mt-1">{validationErrors.name_en}</p>
                    )}
                  </div>
                  <Button
                    type="button"
                    onClick={() => translateText(newPackage.name_en, 'en', 'ar', 'name_ar')}
                    disabled={!newPackage.name_en || translatingField === 'name_ar'}
                    variant="outline"
                    size="sm"
                    className="border-[#2a2a4e] text-blue-400 hover:bg-blue-500/20 w-10 h-10 p-0"
                    title={language === 'ar' ? 'ترجمة إلى العربية' : 'Translate to Arabic'}
                  >
                    {translatingField === 'name_ar' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Languages className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* الملاحظات بالعربية */}
              <div>
                <label className="block text-sm text-[#808080] mb-2">
                  {language === 'ar' ? 'الملاحظات بالعربية' : 'Notes (Arabic)'}
                </label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <textarea
                      value={newPackage.notes_ar}
                      onChange={(e) => updatePackageField('notes_ar', e.target.value)}
                      placeholder={language === 'ar' ? 'أدخل الملاحظات بالعربية...' : 'Enter Arabic notes...'}
                      className={`w-full bg-[#0a0a0a] border rounded-xl text-white p-3 min-h-[80px] resize-none ${
                        validationErrors.notes_ar ? 'border-red-500' : 'border-[#2a2a4e]'
                      }`}
                    />
                    {validationErrors.notes_ar && (
                      <p className="text-xs text-red-400 mt-1">{validationErrors.notes_ar}</p>
                    )}
                  </div>
                  <Button
                    type="button"
                    onClick={() => translateText(newPackage.notes_ar, 'ar', 'en', 'notes_en')}
                    disabled={!newPackage.notes_ar || translatingField === 'notes_en'}
                    variant="outline"
                    size="sm"
                    className="border-[#2a2a4e] text-blue-400 hover:bg-blue-500/20 w-10 h-10 p-0 self-start mt-1"
                    title={language === 'ar' ? 'ترجمة إلى الإنجليزية' : 'Translate to English'}
                  >
                    {translatingField === 'notes_en' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Languages className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* الملاحظات بالإنجليزية */}
              <div>
                <label className="block text-sm text-[#808080] mb-2">
                  {language === 'ar' ? 'الملاحظات بالإنجليزية' : 'Notes (English)'}
                </label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <textarea
                      value={newPackage.notes_en}
                      onChange={(e) => updatePackageField('notes_en', e.target.value)}
                      placeholder={language === 'ar' ? 'أدخل الملاحظات بالإنجليزية...' : 'Enter English notes...'}
                      className={`w-full bg-[#0a0a0a] border rounded-xl text-white p-3 min-h-[80px] resize-none ${
                        validationErrors.notes_en ? 'border-red-500' : 'border-[#2a2a4e]'
                      }`}
                      dir="ltr"
                    />
                    {validationErrors.notes_en && (
                      <p className="text-xs text-red-400 mt-1">{validationErrors.notes_en}</p>
                    )}
                  </div>
                  <Button
                    type="button"
                    onClick={() => translateText(newPackage.notes_en, 'en', 'ar', 'notes_ar')}
                    disabled={!newPackage.notes_en || translatingField === 'notes_ar'}
                    variant="outline"
                    size="sm"
                    className="border-[#2a2a4e] text-blue-400 hover:bg-blue-500/20 w-10 h-10 p-0 self-start mt-1"
                    title={language === 'ar' ? 'ترجمة إلى العربية' : 'Translate to Arabic'}
                  >
                    {translatingField === 'notes_ar' ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Languages className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#808080] mb-2">
                    {language === 'ar' ? 'عدد الأنشطة' : 'Activities Count'}
                  </label>
                  <Input
                    type="number"
                    min="1"
                    value={newPackage.activities_count}
                    onChange={(e) => setNewPackage({ ...newPackage, activities_count: e.target.value })}
                    className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#808080] mb-2">
                    {language === 'ar' ? 'عدد الإقامات' : 'Visas Count'}
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={newPackage.visas_count}
                    onChange={(e) => setNewPackage({ ...newPackage, visas_count: e.target.value })}
                    className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#808080] mb-2">
                  {language === 'ar' ? 'نوع السعر' : 'Price Type'}
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setNewPackage({ ...newPackage, price_type: 'fixed' })}
                    className={`flex-1 py-3 rounded-xl border transition-all flex items-center justify-center gap-2 ${
                      newPackage.price_type === 'fixed'
                        ? 'bg-blue-500/20 border-blue-500 text-blue-400'
                        : 'bg-[#0a0a0a] border-[#2a2a4e] text-[#808080] hover:border-[#404060]'
                    }`}
                  >
                    <Check className={`h-4 w-4 ${newPackage.price_type === 'fixed' ? 'opacity-100' : 'opacity-0'}`} />
                    {language === 'ar' ? 'ثابت (Fixed)' : 'Fixed'}
                  </button>
                  <button
                    onClick={() => setNewPackage({ ...newPackage, price_type: 'range' })}
                    className={`flex-1 py-3 rounded-xl border transition-all flex items-center justify-center gap-2 ${
                      newPackage.price_type === 'range'
                        ? 'bg-orange-500/20 border-orange-500 text-orange-400'
                        : 'bg-[#0a0a0a] border-[#2a2a4e] text-[#808080] hover:border-[#404060]'
                    }`}
                  >
                    <Check className={`h-4 w-4 ${newPackage.price_type === 'range' ? 'opacity-100' : 'opacity-0'}`} />
                    {language === 'ar' ? 'نطاق (Range)' : 'Range'}
                  </button>
                </div>
              </div>

              {newPackage.price_type === 'fixed' ? (
                <div>
                  <label className="block text-sm text-[#808080] mb-2">
                    {language === 'ar' ? 'السعر' : 'Price'} *
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={newPackage.price_fixed}
                    onChange={(e) => setNewPackage({ ...newPackage, price_fixed: e.target.value })}
                    placeholder="0"
                    className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'من' : 'From'} *
                    </label>
                    <Input
                      type="number"
                      min="0"
                      value={newPackage.price_min}
                      onChange={(e) => setNewPackage({ ...newPackage, price_min: e.target.value })}
                      placeholder="0"
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#808080] mb-2">
                      {language === 'ar' ? 'إلى' : 'To'} *
                    </label>
                    <Input
                      type="number"
                      min="0"
                      value={newPackage.price_max}
                      onChange={(e) => setNewPackage({ ...newPackage, price_max: e.target.value })}
                      placeholder="0"
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-[#2a2a4e]">
              <Button
                onClick={() => setShowPackageModal(false)}
                variant="outline"
                className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e]"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={savePackage}
                disabled={savingPackage || Object.keys(validationErrors).length > 0}
                className="bg-amber-500 hover:bg-amber-600 text-black gap-2"
              >
                {savingPackage ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {editingPackage 
                  ? (language === 'ar' ? 'تحديث' : 'Update')
                  : (language === 'ar' ? 'إضافة' : 'Add')
                }
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Print Language Chooser */}
      {printTarget && (
        <div
          className="fixed inset-0 bg-black/80 z-[110] flex items-center justify-center p-4"
          onClick={() => setPrintTarget(null)}
        >
          <div
            className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                  <Printer className="h-5 w-5 text-indigo-400" />
                </div>
                <h3 className="text-lg font-bold text-white">
                  {language === 'ar' ? 'اختر لغة الطباعة' : 'Choose print language'}
                </h3>
              </div>
              <button
                onClick={() => setPrintTarget(null)}
                className="w-8 h-8 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-red-500/20 transition-colors"
                data-testid="close-print-lang-modal"
              >
                <X className="h-4 w-4 text-[#808080]" />
              </button>
            </div>
            <p className="text-sm text-[#808080] mb-5">
              {language === 'ar'
                ? 'كل حقول ملف PDF ستُطبع باللغة التي تختارها.'
                : 'All fields in the PDF will be printed in the language you choose.'}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  const t = printTarget;
                  setPrintTarget(null);
                  printRakezActivity(t, { language: 'ar' });
                }}
                className="group flex flex-col items-center gap-2 p-5 rounded-xl bg-[#0a0a0a] border-2 border-[#2a2a4e] hover:border-indigo-500 hover:bg-indigo-500/10 transition-all"
                data-testid="print-lang-ar-btn"
              >
                <div className="text-3xl font-bold text-white group-hover:text-indigo-300">ع</div>
                <div className="text-sm font-semibold text-white">العربية</div>
                <div className="text-xs text-[#606060]">Arabic PDF</div>
              </button>
              <button
                onClick={() => {
                  const t = printTarget;
                  setPrintTarget(null);
                  printRakezActivity(t, { language: 'en' });
                }}
                className="group flex flex-col items-center gap-2 p-5 rounded-xl bg-[#0a0a0a] border-2 border-[#2a2a4e] hover:border-indigo-500 hover:bg-indigo-500/10 transition-all"
                data-testid="print-lang-en-btn"
              >
                <div className="text-3xl font-bold text-white group-hover:text-indigo-300">EN</div>
                <div className="text-sm font-semibold text-white">English</div>
                <div className="text-xs text-[#606060]">إنجليزي PDF</div>
              </button>
            </div>
          </div>
        </div>
      )}
      </section>
    </DashboardLayout>
  );
}

// Detail Item Component
function DetailItem({ label, value, fullWidth, dir, highlight }) {
  const isEmpty = !value || value === 'nan' || value === 'None' || value === '';
  
  return (
    <div className={fullWidth ? 'col-span-2 mt-2' : ''}>
      <p className="text-xs text-[#606060] mb-1">{label}</p>
      <p 
        className={`text-sm ${
          isEmpty ? 'text-[#404040] italic' : 
          highlight === 'green' ? 'text-emerald-400' :
          highlight === 'red' ? 'text-red-400' : 'text-white'
        }`}
        dir={dir}
      >
        {isEmpty ? '-' : value}
      </p>
    </div>
  );
}
